import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { PlanogramPlan, PlanFilters, PlanPaginationParams } from '../../types/plan'
import planService from '../../services/planService'

interface PlanogramPlansState {
  plans: PlanogramPlan[]
  currentPlan: PlanogramPlan | null
  loading: boolean
  error: string | null
  filters: PlanFilters
  pagination: {
    currentPage: number
    pageSize: number
    totalCount: number
  }
}

const initialState: PlanogramPlansState = {
  plans: [],
  currentPlan: null,
  loading: false,
  error: null,
  filters: {
    planType: undefined,
    templateReference: undefined,
    status: undefined,
    templateOwner: undefined,
    generateTimeRange: undefined,
    startTimeRange: undefined,
    keyword: undefined
  },
  pagination: {
    currentPage: 1,
    pageSize: 20,
    totalCount: 0,
  },
}

// Async thunks
export const fetchPlanList = createAsyncThunk(
  'planogramPlans/fetchPlanList',
  async (params: { filters: PlanFilters; pagination: { currentPage: number; pageSize: number } }) => {
    // Convert FilterCriteria to PlanFilters
    const planFilters: PlanFilters = {
      planType: params.filters?.planType,
      templateReference: params.filters?.templateReference,
      status: params.filters?.status,
      templateOwner: params.filters?.templateOwner,
      keyword: params.filters?.keyword,
      // 注意：这里不直接传递 generateTimeRange 和 startTimeRange，
      // 而是在下面转换为时间戳格式的字段
      createStartTime: undefined,
      createEndTime: undefined,
      planStartTime: undefined,
      planEndTime: undefined
    }
    
    // 处理 generateTimeRange，转换为时间戳格式
    let generateTimeRange = params.filters?.generateTimeRange;
    if (generateTimeRange && generateTimeRange.length === 2) {
      planFilters.createStartTime = generateTimeRange[0].valueOf(); // 转换为时间戳
      planFilters.createEndTime = generateTimeRange[1].valueOf();   // 转换为时间戳
    }
    
    // 处理 startTimeRange，转换为时间戳格式
    let startTimeRange = params.filters?.startTimeRange;
    if (startTimeRange && startTimeRange.length === 2) {
      planFilters.planStartTime = startTimeRange[0].valueOf();     // 转换为时间戳
      planFilters.planEndTime = startTimeRange[1].valueOf();       // 转换为时间戳
    }

    let lastMilestonePlanTimeRange = params.filters?.lastMilestonePlanTimeRange;
    if (lastMilestonePlanTimeRange && lastMilestonePlanTimeRange.length === 2) {
      planFilters.lastMilestonePlanStartTimeStart = lastMilestonePlanTimeRange[0].valueOf(); // 转换为时间戳
      planFilters.lastMilestonePlanStartTimeEnd = lastMilestonePlanTimeRange[1].valueOf();   // 转换为时间戳
    }
    
    const paginationParams: PlanPaginationParams = {
      currentPage: params.pagination.currentPage,
      pageSize: params.pagination.pageSize,
      sortBy: 'gmtModified',
      sortOrder: 'desc'
    }
    
    const response = await planService.getPlans(planFilters, paginationParams)
    return response
  }
)

export const fetchPlanDetail = createAsyncThunk(
  'planogramPlans/fetchPlanDetail',
  async (id: string) => {
    const response = await planService.getPlanById(id)
    return response
  }
)

export const activatePlan = createAsyncThunk(
  'planogramPlans/activatePlan',
  async (params: {
    id: string
    projectOwner: string
    plannedStartTime: number
    description?: string
  }) => {
    // 将参数转换为表单数据格式
    const formData = {
      projectOwner: params.projectOwner,
      plannedStartTime: new Date(params.plannedStartTime),
      description: params.description
    };
    
    const response = await planService.activatePlan(params.id, formData)
    return response
  }
)

const planogramPlansSlice = createSlice({
  name: 'planogramPlans',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<PlanFilters>) => {
      state.filters = action.payload
    },
    setPagination: (state, action: PayloadAction<Partial<{
      currentPage: number
      pageSize: number
      totalCount: number
    }>>) => {
      state.pagination = { ...state.pagination, ...action.payload }
    },
    clearCurrentPlan: (state) => {
      state.currentPlan = null
    },
    clearError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch plan list
      .addCase(fetchPlanList.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchPlanList.fulfilled, (state, action) => {
        state.loading = false
        if (action.payload.data) {
          state.plans = action.payload.data.tableData
          state.pagination = action.payload.data.paging
        }
      })
      .addCase(fetchPlanList.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch plan list'
      })
      // Fetch plan detail
      .addCase(fetchPlanDetail.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchPlanDetail.fulfilled, (state, action) => {
        state.loading = false
        if (action.payload.data) {
          state.currentPlan = action.payload.data
        }
      })
      .addCase(fetchPlanDetail.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch plan detail'
      })
      // Activate plan
      .addCase(activatePlan.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(activatePlan.fulfilled, (state, _action) => {
        state.loading = false
        // For now, just reload the data since mock service doesn't return updated plan
        // In real implementation, this would update the specific plan
      })
      .addCase(activatePlan.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to activate plan'
      })
  },
})

// Export fetchPlanList as fetchPlans for calendar component compatibility
export const fetchPlans = fetchPlanList

export const { setFilters, setPagination, clearCurrentPlan, clearError } = planogramPlansSlice.actions
export default planogramPlansSlice.reducer